'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { setStateValue } from '@/app/redux/slices/configGymSlice';
import {
  setItems,
  setTotalValue,
  setBasketItemsQty,
} from '@/app/redux/slices/configBasketSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesRight } from '@fortawesome/pro-solid-svg-icons';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import {
  Grid,
  GridItem,
  Flex,
  Button,
  Center,
  Text,
  Box,
  useBreakpointValue,
} from '@chakra-ui/react';
import costsData from '/public/data/costs.json';
import useFBGymAddresses from '@/app/hooks/gym/useFBGymAddresses';
import useAddBasketToStore from '@/app/hooks/basket/useAddBasketToStore';
import useAddOrderToStore from '@/app/hooks/basket/useAddOrderToStore';
import useaddOrUpdateCustomer from '@/app/hooks/basket/useAddCustomerToStore';

const buttonNext = {
  width: '100%',
  borderRadius: { base: '4vw', md: '2.5vw' },
  bg: '#0cc6de',
  color: 'white',
  fontSize: { base: '2vw', md: '1vw' },
  fontWeight: 600,
  padding: { base: '2vw 0', md: '1.5vw 0' },
  _hover: {
    cursor: 'pointer',
    bg: '#0cc6de80',
  },
};

export default function GymJobDetails({ isFormValid, showStripe }) {
  const router = useRouter();
  const isMobile = useSelector((state) => state.gym.isMobile);
  const [isItemisedVisible, setIsItemisedVisible] = useState(false); // State for toggling visibility
  const { FBGymAddresses, loading, error } = useFBGymAddresses();
  const addBasketToStore = useAddBasketToStore();
  const addOrderToStore = useAddOrderToStore();
  const { addOrUpdateCustomer } = useaddOrUpdateCustomer();
  const dispatch = useDispatch();
  const gymData = useSelector((state) => state.gym);
  const basket = useSelector((state) => state.basket.basketData);
  const basketDel = useSelector((state) => state.basket.deliveryData);
  const basketBill = useSelector((state) => state.basket.billingData);
  const delCost = useSelector((state) => state.basket.delCost);
  const delMethod = useSelector((state) => state.basket.delMethod);
  const customerData = useSelector((state) => state.basket.customerData);
  const surveyChoices = gymData.survey;
  const service = gymData.serviceOpt;
  const installRequired = gymData.install;
  const salePrice = costsData.find((item) => item.name === 'sale');
  const [saleTotal, setSaleTotal] = useState(0);
  const [customerID, setCustomerID] = useState(null);

  const isCheckoutValid = gymData.isCheckoutValid;

  const addToBasket = () => {
    const basketItem = {
      name: 'Gym Mirrors',
      price: saleTotal,
      qty: 1,
      jobInfo: {},
      jobDesc: 'Gym Mirror Service',
      id: gymData.gymJobID,
    };
    console.log(basketItem);
    sessionStorage.setItem('basketIdShort', basket.basketID.substring(0, 8));
    dispatch(setItems([basketItem]));
    dispatch(setTotalValue(saleTotal));
    dispatch(setBasketItemsQty(1));

    console.log('add current gym job to a basket');
  };
  const showItemised = () => {
    setIsItemisedVisible((prev) => !prev); // Toggle visibility
    console.log(isItemisedVisible);
  };
  // Find the selected survey type
  const selectedSurvey = surveyChoices.find((choice) => choice.selected);
  const survey = selectedSurvey ? selectedSurvey.type : null;

  let Lsale = salePrice.L * gymData.largeQty;
  let XLsale = salePrice.XL * gymData.XLQty;
  let XXLsale = salePrice.XXL * gymData.XXLQty;
  let mirrorQty = gymData.mirrorTotal;
  let surveyPrice = salePrice.survey;

  // Calculate bespoke mirror data
  const bespokeSummary = gymData.bespokeMirrors.reduce(
    (acc, mirror) => {
      acc.totalPrice += mirror.qty * mirror.price;
      acc.totalQty += mirror.qty;
      return acc;
    },
    { totalPrice: 0, totalQty: 0 }
  );
  let bespokeTotalPrice = parseFloat(bespokeSummary.totalPrice.toFixed(2));
  let bespokeTotalQty = bespokeSummary.totalQty;
  let totalMirrorQty = bespokeTotalQty + mirrorQty;

  // Calculate Installation and Delivery Cost
  const minInstall = salePrice.standard - salePrice.L;
  const delivery = service === 'basic' ? salePrice.delivery : 0;
  const install =
    service === 'standard' || service === 'premium'
      ? minInstall + (totalMirrorQty - 1) * salePrice.fitting
      : 0;

  // Calculate fittings data
  const fittingsSummary = gymData.fittings.reduce(
    (acc, fitting) => {
      const price = salePrice[fitting.type] || 0;
      acc.fittingsPrice += fitting.qty * price;
      acc.fittingsQty += fitting.qty;
      return acc;
    },
    { fittingsPrice: 0, fittingsQty: 0 }
  );
  let totalFittingsPrice = parseFloat(fittingsSummary.fittingsPrice.toFixed(2));

  // Survey Fee based on selected survey type
  const surveyFee = survey === 'paid' ? surveyPrice : 0;

  // Calculate and dispatch total sale amount
  useEffect(() => {
    const calculatedSaleTotal =
      Lsale +
      XLsale +
      XXLsale +
      bespokeTotalPrice +
      delivery +
      install +
      surveyFee +
      totalFittingsPrice;
    setSaleTotal(calculatedSaleTotal);
    dispatch(setStateValue({ key: 'saleTotal', value: calculatedSaleTotal }));
  }, [
    dispatch,
    Lsale,
    XLsale,
    XXLsale,
    bespokeTotalPrice,
    delivery,
    install,
    surveyFee,
    totalFittingsPrice,
  ]);

  const handleNext = async () => {
    dispatch(setStateValue({ key: 'checkoutLoading', value: true }));
    if (isCheckoutValid && isFormValid) {
      try {
        // Adding all the data to the Firebase store
        const deliveryID = await FBGymAddresses('delivery'); // Update delivery data
        const billingID = await FBGymAddresses('billing'); // Update billing data
        await addBasketToStore(basket, 'baskets'); // Ensure basket data is stored

        // Update or create customer record
        const newCustomerID = await addOrUpdateCustomer(
          basket.basketID,
          billingID,
          deliveryID,
          customerData,
          customerID // Pass the existing customer ID if available
        );
        if (newCustomerID) {
          setCustomerID(newCustomerID); // Update the local state
        }
        const billInfo = createBillInfoData(); // create the billInfoData for orderData
        // add the Order to ht firebase Store
        const orderData = {
          billInfo: billInfo,
          delAddInfo: basketDel,
          billAddInfo: basketBill,
          customer: customerData,
          basketID: basket.basketID,
        };
        console.log(orderData);
        await addOrderToStore(orderData); // Create Order for the current basket data
        showStripe();
      } catch (err) {
        console.error('Failed to update gym data:', err);
      }
    } else {
      addToBasket();
      dispatch(setStateValue({ key: 'progressOpt', value: 'Payment' }));

      router.push('/summary'); // Redirect to the summary page
    }
  };
  const createBillInfoData = () => {
    return {
      itemsQty: basket.basketItemsQty,
      billValue: basket.basketTotalValue,
      delCost: delCost,
      delMethod: delMethod,
      paymentStatus: 'pending',
    };
  };

  useEffect(() => {
    if (customerID) {
      console.log('Customer ID after state update:', customerID);
    }
  }, [customerID]);

  return (
    <Box
      height="99%" // Collapse height for mobile
      background="white"
      borderRadius={{ base: '6vw', md: '2vw' }}
      flexGrow={1}
      flexDirection="column"
      margin={{ base: '0 0vw 0vw', md: '0vw 4vw 2vw' }}
      boxShadow="5px 6px 15px rgba(0, 0, 0, 0.3)"
      fontSize={{ base: '4vw', md: '1vw' }}
      padding={{ base: '1vw 0', md: '0' }}
    >
      <Grid
        templateRows="auto" // Content rows are auto-sized, last row pushes footer to the bottom
        gap={4}
        padding={{ base: '0 5vw', md: '2vw' }}
        margin={{ base: '0', md: '0 0 1vw' }}
        display="flex"
        flexDirection="column"
        height="100%"
      >
        {/* Individual Items */}
        <GridItem
          display={isItemisedVisible || !isMobile ? 'inline' : 'None'}
          id="viewDetails"
          margin={{ base: '4vw 0 0', md: '0vw' }}
          onClick={showItemised}
        >
          <Flex
            justifyContent="space-between"
            alignItems="center"
            fontWeight="600"
          >
            <Text fontWeight="700">Service Option:</Text>
            <Text textAlign="right">{gymData.serviceOpt}</Text>
          </Flex>
          {totalMirrorQty > 0 && (
            <Box borderBottom="1px solid #00000080" padding="0.5vw 0">
              <Text fontWeight="600">Mirrors:</Text>
              {gymData.largeQty > 0 && (
                <Box fontWeight="400">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text>Large Std x{gymData.largeQty}</Text>
                    <Text textAlign="right">£ {Lsale.toFixed(2)}</Text>
                  </Flex>
                </Box>
              )}
              {gymData.XLQty > 0 && (
                <Box fontWeight="400">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text>XLarge Std x{gymData.XLQty}</Text>
                    <Text textAlign="right">£ {XLsale.toFixed(2)}</Text>
                  </Flex>
                </Box>
              )}
              {gymData.XXLQty > 0 && (
                <Box fontWeight="400">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text>XXLarge Std x{gymData.XXLQty}</Text>
                    <Text textAlign="right">£ {XXLsale.toFixed(2)}</Text>
                  </Flex>
                </Box>
              )}
              {/* Bespoke Mirrors */}
              {gymData.bespokeMirrors.map((mirror, index) => (
                <Box key={index} fontWeight="400">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text>
                      Bespoke {index + 1}: x{mirror.qty}
                    </Text>
                    <Text fontSize={{ base: '3vw', md: '0.7vw' }}>
                      {mirror.width}mm x {mirror.height}mm
                    </Text>
                    <Text textAlign="right">
                      £ {(mirror.qty * mirror.price).toFixed(2)}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </Box>
          )}
          {install > 0 && (
            <Box borderBottom="1px solid #00000080" padding="0.5vw 0">
              <Text fontWeight="600">Installation:</Text>
              <Box fontWeight="400">
                <Flex justifyContent="space-between" alignItems="center">
                  <Text>Mirrors to be Installed x{totalMirrorQty}</Text>
                  <Text textAlign="right">£ {install.toFixed(2)}</Text>
                </Flex>
              </Box>
            </Box>
          )}
          {totalFittingsPrice > 0 && (
            <Box borderBottom="1px solid #00000080" padding="0.5vw 0">
              <Text fontWeight="600">Fittings:</Text>
              {gymData.fittings
                .filter((fitting) => fitting.qty > 0) // Filter out fittings with qty > 0
                .map((fitting, index) => (
                  <Box key={index} fontWeight="400">
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text>
                        {fitting.type}: x{fitting.qty}
                      </Text>
                      <Text textAlign="right">
                        £ {(fitting.qty * fitting.price).toFixed(2)}
                      </Text>
                    </Flex>
                  </Box>
                ))}
            </Box>
          )}
          {(installRequired === 'yes' ||
            survey === 'paid' ||
            (gymData.survey && gymData.survey.complete)) && (
            <Box borderBottom="1px solid #00000080" padding="0.5vw 0">
              <Text fontWeight="600">Survey:</Text>
              {gymData.survey && (
                <Box fontWeight="400">
                  <Flex justifyContent="space-between" alignItems="center">
                    {survey === 'pending' && <Text>Deferred survey</Text>}
                    {survey === 'quick' && <Text>Quick self-survey</Text>}
                    {survey === 'diy' && <Text>In-depth self-survey</Text>}
                    {survey === 'paid' && <Text>Professional survey</Text>}

                    <Text textAlign="right">£ {surveyFee.toFixed(2)}</Text>
                  </Flex>
                </Box>
              )}
            </Box>
          )}
        </GridItem>
        {/* Delviery Details */}
        <GridItem width="100%">
          <Flex
            direction="column"
            justifyContent="space-between"
            alignItems="stretch"
          >
            {service == 'basic' && (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                padding="0.5vw 0"
              >
                <Text>Delivery</Text>
                <Text>£ {delivery.toFixed(2)}</Text>
              </Flex>
            )}
          </Flex>
        </GridItem>
        {/* Final Total Always visible */}
        <GridItem
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          height="100%"
        >
          {/* Order Total */}
          <Grid templateColumns={'40% 1fr'}>
            <GridItem
              display="flex"
              flexDirection="column"
              justifyContent="flex-end"
              alignItems="flex-start"
            >
              {isMobile && !isItemisedVisible && (
                <Flex
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="center"
                  onClick={showItemised}
                  paddingBottom="4vw"
                >
                  <Text fontWeight="400" lineHeight="4vw">
                    View Details
                  </Text>
                  <FontAwesomeIcon icon={faAngleDown} fontSize="5vw" />
                </Flex>
              )}
              {((isMobile && isItemisedVisible) || !isMobile) && (
                <Flex
                  alignItems="flex-start"
                  direction="column"
                  justifyContent="center"
                  height="100%"
                >
                  <Text fontWeight="700" onClick={showItemised}>
                    Order Total
                  </Text>
                </Flex>
              )}
            </GridItem>
            <GridItem
              display="flex"
              flexDirection="column"
              justifyContent="flex-end"
              alignItems="flex-end"
              paddingBottom={{ base: '2vw', md: '0' }}
            >
              <Text
                textAlign="right"
                fontSize={{ base: '8vw', md: '2vw' }}
                fontWeight="700"
                lineHeight={{ base: '8vw', md: '2vw' }}
              >
                £ {saleTotal.toFixed(2)}
              </Text>
              <Text
                textAlign="right"
                fontSize={{ base: '3vw', md: '0.7vw' }}
                fontWeight="700"
              >
                Incl VAT
              </Text>
            </GridItem>
          </Grid>
        </GridItem>

        {/* Proceed to Checkout Button */}
        {isCheckoutValid && (
          <GridItem display="flex">
            <Button
              {...buttonNext}
              onClick={handleNext}
              margin={{ base: '0 0 4vw', md: '0' }}
            >
              <Box
                as="span"
                flex="1"
                textAlign="center"
                ps="1.5vw"
                fontSize={{ base: '4vw', md: '1vw' }}
              >
                {isFormValid ? 'Make Payment' : 'Proceed to Checkout'}
              </Box>
              <Box
                pe={{ base: '4vw', md: '1vw' }}
                fontSize={{ base: '4vw', md: '1.5vw' }}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </Box>
            </Button>
          </GridItem>
        )}
      </Grid>
    </Box>
  );
}
